import { ethers } from "ethers";
import farmAbi from "../../abi/farm.json";
import farmActionAbi from "../../abi/farmAction.json";
import factoryV3Abi from "../../abi/factoryV3.json";
import erc20Abi from "../../abi/erc20.json";
import {
  showErrorAlert as showAlert,
  showErrorAlert,
  showPendingAlert,
} from "../showAlert";
import { isSupportedChain, SupportedChainId } from "../constant/chains";
import { RPC_PROVIDERS } from "../providers";

export const registerDepositIntoFarm = async (
  accountAddress: string,
  farmAddress: string,
  amount: number,
  tokenAddress: string,
  tokenDecimal: number,
  tokenSymbol: string,
  chainId: number,
  provider: any
) => {
  if (!isSupportedChain(chainId)) {
    showAlert("Please switch to correct network.");
  } else {
    const depositAmount = ethers.utils.parseUnits(
      amount.toString(),
      tokenDecimal
    );
    const signer = provider.getSigner(accountAddress);
    const usdc = new ethers.Contract(tokenAddress, erc20Abi, signer);
    const farm = new ethers.Contract(farmAddress, farmAbi, signer);
    const usdcBalance = await usdc.balanceOf(accountAddress);
    const allowance = await usdc.allowance(accountAddress, farmAddress);

    if (usdcBalance.lt(depositAmount)) {
      showAlert(`Your ${tokenSymbol} balance is not enough.`);
    } else {
      if (allowance.lt(depositAmount)) {
        try {
          const approveRes = await usdc.approve(farmAddress, depositAmount);
          await showPendingAlert(
            approveRes.hash,
            `Grant ${tokenSymbol} Permission Succeed`,
            provider
          );
        } catch (error: any) {
          if (error.message.includes("User denied transaction signature")) {
            showErrorAlert("You denied the transaction.");
          } else {
            showErrorAlert(`Grant ${tokenSymbol} Permission Failed`);
          }
        }
      }

      try {
        const res = await farm.connect(signer).registerDeposit(depositAmount);
        await showPendingAlert(res.hash, "Register Deposit Succeed", provider);
      } catch (error: any) {
        if (error.message.includes("User denied transaction signature")) {
          showErrorAlert("You denied the transaction.");
        } else {
          showErrorAlert("Register Deposit Failed");
        }
      }
    }
  }
};

export const depositIntoFarm = async (
  accountAddress: string,
  farmAddress: string,
  amount: number,
  tokenAddress: string,
  tokenDecimal: number,
  tokenSymbol: string,
  chainId: number,
  provider: any
) => {
  if (!isSupportedChain(chainId)) {
    showAlert("Please switch to correct network.");
  } else {
    const depositAmount = ethers.utils.parseUnits(
      amount.toString(),
      tokenDecimal
    );

    const signer = provider.getSigner(accountAddress);
    const usdc = new ethers.Contract(tokenAddress, erc20Abi, signer);
    const farm = new ethers.Contract(farmAddress, farmAbi, signer);
    const usdcBalance = await usdc.balanceOf(accountAddress);
    const allowance = await usdc.allowance(accountAddress, farmAddress);

    if (usdcBalance.lt(depositAmount)) {
      showAlert(`Your ${tokenSymbol} balance is not enough.`);
    } else {
      if (allowance.lt(depositAmount)) {
        try {
          const approveRes = await usdc.approve(farmAddress, depositAmount);
          await showPendingAlert(
            approveRes.hash,
            `Grant ${tokenSymbol} Permission Succeed`,
            provider
          );
        } catch (error: any) {
          if (error.message.includes("User denied transaction signature")) {
            showErrorAlert("You denied the transaction.");
          } else {
            showErrorAlert(`Grant ${tokenSymbol} Permission Failed`);
          }
        }
      }

      try {
        const res = await farm.connect(signer).deposit(depositAmount);
        await showPendingAlert(res.hash, "Deposit Succeed", provider);
      } catch (error: any) {
        if (error.message.includes("User denied transaction signature")) {
          showErrorAlert("You denied the transaction.");
        } else {
          showErrorAlert("Deposit Failed");
        }
      }
    }
  }
};

export const registerWithdrawFromFarm = async (
  accountAddress: string,
  farmAddress: string,
  amount: number,
  tokenDecimal: number,
  tokenSymbol: string,
  chainId: number,
  provider: any
) => {
  if (!isSupportedChain(chainId)) {
    showAlert("Please switch to correct network.");
  } else {
    const withdrawAmount = ethers.utils.parseUnits(
      amount.toString(),
      tokenDecimal
    );
    const signer = provider.getSigner(accountAddress);
    const farm = new ethers.Contract(farmAddress, farmAbi, signer);
    const canWithdrawShares = await farm.estimateAvailableShares(
      accountAddress
    );
    const preRound = +(await farm.round()).toString() - 1;
    const withdrawShares = await farm.getSharesByDepositAmount(
      withdrawAmount,
      preRound
    );
    if (canWithdrawShares.lt(withdrawShares)) {
      showAlert("Your amount has exceeded your withdrawable balance");
    } else {
      try {
        const res = await farm.connect(signer).registerWithdraw(withdrawShares);
        await showPendingAlert(
          res.hash,
          `Register Withdraw ${tokenSymbol} Succeed`,
          provider
        );
      } catch (error: any) {
        if (error.message.includes("Not in whitelist")) {
          showErrorAlert("Your account address is not in the whitelist.");
        } else if (
          error.message.includes("User denied transaction signature")
        ) {
          showErrorAlert("You denied the transaction.");
        } else {
          showErrorAlert("Registered Withdraw Failed");
        }
      }
    }
  }
};

export const withdrawFromFarm = async (
  accountAddress: string,
  farmAddress: string,
  amount: number,
  tokenDecimal: number,
  tokenSymbol: string,
  chainId: number,
  provider: any
) => {
  if (!isSupportedChain(chainId)) {
    showAlert("Please switch to correct network.");
  } else {
    const withdrawAmount = ethers.utils.parseUnits(
      amount.toString(),
      tokenDecimal
    );
    const signer = provider.getSigner(accountAddress);
    const farm = new ethers.Contract(farmAddress, farmAbi, signer);
    const canWithdrawShares = await farm.estimateAvailableShares(
      accountAddress
    );
    const preRound = +(await farm.round()).toString() - 1;
    const withdrawShares = await farm.getSharesByDepositAmount(
      withdrawAmount,
      preRound
    );
    if (canWithdrawShares.lt(withdrawShares)) {
      showAlert("Your amount has exceeded your withdrawable balance");
    } else {
      try {
        const res = await farm.connect(signer).withdraw(withdrawShares);
        await showPendingAlert(
          res.hash,
          `Withdraw ${tokenSymbol} Succeed`,
          provider
        );
      } catch (error: any) {
        if (error.message.includes("Not in whitelist")) {
          showErrorAlert("Your account address is not in the whitelist.");
        } else if (
          error.message.includes("User denied transaction signature")
        ) {
          showErrorAlert("You denied the transaction.");
        } else {
          showErrorAlert("Withdraw Failed");
        }
      }
    }
  }
};

export const getEstimatedBalanceInFarm = async (
  accountAddress: string,
  farmAddress: string,
  tokenDecimal: number,
  provider: any
) => {
  const farm = new ethers.Contract(farmAddress, farmAbi, provider);
  const balance = ethers.utils.formatUnits(
    await farm.estimateTotalAssets(accountAddress),
    tokenDecimal
  );

  return balance;
};

export const getPreRoundSharePriceOfFarm = async (
  farmAddress: string,
  tokenDecimal: number,
  chainId: number
) => {
  const provider = isSupportedChain(chainId)
    ? RPC_PROVIDERS[chainId]
    : RPC_PROVIDERS[SupportedChainId.ARBITRUM_ONE];
  const farm = new ethers.Contract(farmAddress, farmAbi, provider);
  const round = await farm.round();
  const sharePrice = ethers.utils.formatUnits(
    await farm.roundSharePrice(+round - 1),
    tokenDecimal
  );

  return sharePrice;
};

export const getTotalSupplyOfFarm = async (
  farmAddress: string,
  tokenDecimal: number,
  chainId: number
) => {
  const provider = isSupportedChain(chainId)
    ? RPC_PROVIDERS[chainId]
    : RPC_PROVIDERS[SupportedChainId.ARBITRUM_ONE];
  const farm = new ethers.Contract(farmAddress, farmAbi, provider);
  return ethers.utils.formatUnits(
    (await farm.totalAssets()).toString(),
    tokenDecimal
  );
};

export const getFirstRolloverTime = async (
  farmActionAddress: string,
  chainId: number
) => {
  const provider = isSupportedChain(chainId)
    ? RPC_PROVIDERS[chainId]
    : RPC_PROVIDERS[SupportedChainId.ARBITRUM_ONE];

  const filter = {
    address: farmActionAddress,
    fromBlock: 0,
  };

  const res = await provider.getLogs(filter);
  const iface = new ethers.utils.Interface(farmActionAbi);

  const parsedEvents = res.map((log) => {
    return { ...iface.parseLog(log) };
  });

  const times = parsedEvents
    .filter((e) => e.name.toUpperCase() === "OpenPosition".toUpperCase())
    .map((e) => {
      return +e.args.rolloverTime.toString();
    });

  return times[0];
};

export const getPriceRangeOfFarm = async (
  factoryAddress: string,
  farmActionAddress: string,
  chainId: number
) => {
  const provider = isSupportedChain(chainId)
    ? RPC_PROVIDERS[chainId]
    : RPC_PROVIDERS[SupportedChainId.ARBITRUM_ONE];

  const factory = new ethers.Contract(factoryAddress, factoryV3Abi, provider);
  const farmAction = new ethers.Contract(
    farmActionAddress,
    farmActionAbi,
    provider
  );
  const pId = (await farmAction.positionId()).toString();
  const pInfo = await factory.getPositionInfo(pId);

  return {
    upperTickPrice: 1.0001 ** +pInfo.upperTick * 10 ** 12,
    lowerTickPrice: 1.0001 ** +pInfo.lowerTick * 10 ** 12,
  };
};

export const doRollover = async (farmAddress: string, chainId: number) => {
  const provider = isSupportedChain(chainId)
    ? RPC_PROVIDERS[chainId]
    : RPC_PROVIDERS[SupportedChainId.ARBITRUM_ONE];

  await provider.send("hardhat_impersonateAccount", [
    "0x9Cf11F95FfB3F122be512f73918e63187a18A71d",
  ]);

  const signer = provider.getSigner(
    "0x9Cf11F95FfB3F122be512f73918e63187a18A71d"
  );

  const farm = new ethers.Contract(farmAddress, farmAbi, signer);

  const res = await farm.rollOver([10000]);
  console.log(res);

  console.log("rollOver");
};

export const doClose = async (farmAddress: string, chainId: number) => {
  const provider = isSupportedChain(chainId)
    ? RPC_PROVIDERS[chainId]
    : RPC_PROVIDERS[SupportedChainId.ARBITRUM_ONE];

  await provider.send("hardhat_impersonateAccount", [
    "0x9Cf11F95FfB3F122be512f73918e63187a18A71d",
  ]);

  const signer = provider.getSigner(
    "0x9Cf11F95FfB3F122be512f73918e63187a18A71d"
  );

  const farm = new ethers.Contract(farmAddress, farmAbi, signer);

  console.log(farm);

  const res = await farm.connect(signer).closePositions(false);

  console.log(res);

  // await farm.closePositions(false);
};

export const getRegisteredWithdrawnAmountInFarm = async (
  accountAddress: string,
  farmAddress: string,
  tokenDecimal: number,
  provider: any
) => {
  const farm = new ethers.Contract(farmAddress, farmAbi, provider);
  const registerWithdrawShares = await farm.getRegisterWithdrawShares(
    accountAddress
  );
  const currRound = +(await farm.round());

  const registeredWithdrawnAmount = +ethers.utils.formatUnits(
    await farm.getWithdrawAmountByShares(registerWithdrawShares, currRound - 1),
    tokenDecimal
  );

  return registeredWithdrawnAmount;
};

export const getRegisteredDepositAmountInFarm = async (
  accountAddress: string,
  farmAddress: string,
  tokenDecimal: number,
  provider: any
) => {
  const farm = new ethers.Contract(farmAddress, farmAbi, provider);
  const amount = +ethers.utils.formatUnits(
    await farm.getRegisterDepositAssets(accountAddress),
    tokenDecimal
  );

  return amount;
};

export const getCanWithdrawAmountInFarm = async (
  accountAddress: string,
  farmAddress: string,
  tokenDecimal: number,
  provider: any
) => {
  const farm = new ethers.Contract(farmAddress, farmAbi, provider);
  const shares = await farm.estimateAvailableShares(accountAddress);
  const currRound = +(await farm.round());
  const canWithdrawAmount = +ethers.utils.formatUnits(
    await farm.getWithdrawAmountByShares(shares, currRound - 1),
    tokenDecimal
  );

  return canWithdrawAmount;
};

export const getAutoVaultState = async (
  farmAddress: string,
  chainId: number
) => {
  const provider = isSupportedChain(chainId)
    ? RPC_PROVIDERS[chainId]
    : RPC_PROVIDERS[SupportedChainId.ARBITRUM_ONE];

  const farm = new ethers.Contract(farmAddress, farmAbi, provider);
  const state = await farm.state();

  return state;
};
